import {DatePicker, Input, TimePicker} from 'antd'
import locale from 'antd/es/date-picker/locale/it_IT'
import moment from 'moment'
import {Chip, Typography} from '@mui/material'
import {useAppSelector} from 'src/hooks'
import {
  getBodyPartSuggestions,
  getOtherBodyPart,
  getAssumptionColumnOptions,
  getVisibleAssumptionFieldIds,
  SecondaryAssumption,
  SecondaryAssumptionResponseForm as ResponseForm,
  setOtherBodyPart,
  toggleBodyPart
} from 'src/constants/secondaryAssumptions'
import AssumptionTakenChoice from './AssumptionTakenChoice'

// Legacy-stack twin of pages-new/secondaryAssumptions/AssumptionResponseFormNew.
// Only the rendering is duplicated: every rule behind this form (what a choice means, what body
// gets sent, how an existing answer is precompiled) lives in constants/secondaryAssumptions and is
// shared, so the two portals cannot drift apart on the parts that change behaviour.

type AssumptionResponseFormProps = {
  assumption: SecondaryAssumption
  form: ResponseForm
  onChange: (form: ResponseForm) => void
  disabled?: boolean
  // Skips the "Hai assunto la terapia?" question — for the quick-confirm dialog opened by the
  // list's round ✓ button, where the answer is already implied by that tap. The caller is
  // responsible for having set `form.taken` to 'yes' before rendering.
  hideTakenQuestion?: boolean
  // Fired right after the ✓/✗ choice is made, with the resulting form — for the deep-link page,
  // where tapping the round button IS the answer and a separate "Invia risposta" click on top of
  // it would be redundant friction ("no" needs nothing else, "yes" defaults to "taken as
  // planned"). Carries the next form rather than just the value because `onChange` only schedules
  // a state update — reading `form` right after calling it would still see the pre-tap value.
  //
  // Deliberately NOT wired by the "Modifica"/quick-confirm dialogs: those have an explicit
  // Annulla/Conferma contract, and auto-submitting on tap would make "Annulla" lie after an
  // accidental tap.
  onTakenChoice?: (form: ResponseForm) => void
}

const LABEL_STYLE = {fontWeight: 600, marginBottom: 8, display: 'block'} as const

const AssumptionResponseForm: React.FC<AssumptionResponseFormProps> = ({
  assumption,
  form,
  onChange,
  disabled,
  hideTakenQuestion,
  onTakenChoice
}) => {
  const assumptionsColumnOptions = useAppSelector(
    state => getAssumptionColumnOptions(state.pspConfigReducer.data)
  )
  const visibleFields = getVisibleAssumptionFieldIds(assumptionsColumnOptions)
  const configuredBodyParts = useAppSelector(
    state => state.pspConfigReducer.data.therapyDiaryConfigurations?.secondaryTherapiesConfiguration?.bodyParts
  )
  const bodyPartSuggestions = getBodyPartSuggestions(configuredBodyParts)
  const showDate = visibleFields.has('date')
  const showTime = visibleFields.has('time')
  const showBodyPart = visibleFields.has('bodyPart')
  const showTiming = showDate || showTime

  const onTakenChange = (value: 'yes' | 'no') => {
    const nextForm = {...form, taken: value}
    onChange(nextForm)
    onTakenChoice?.(nextForm)
  }

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: 24}}>
      {!hideTakenQuestion && (
        <div>
          <Typography variant='body2' style={LABEL_STYLE}>Hai assunto la terapia?</Typography>
          <AssumptionTakenChoice
            value={form.taken}
            onChange={onTakenChange}
            disabled={disabled}
          />
        </div>
      )}

      {form.taken === 'yes' && (
        <>
          {showTiming && (
            <div style={{display: 'flex', gap: 16, flexWrap: 'wrap'}}>
              {showDate && (
                <div style={{flex: '1 1 180px'}}>
                  <Typography variant='body2' style={LABEL_STYLE}>Data</Typography>
                  <DatePicker
                    disabled={disabled}
                    format='DD/MM/YYYY'
                    locale={locale}
                    popupStyle={{zIndex: 1400}}
                    style={{width: '100%'}}
                    value={form.takenAt ? moment(form.takenAt) : null}
                    onChange={value => onChange({...form, takenAt: value ? value.toISOString() : null})}
                  />
                </div>
              )}
              {showTime && (
                <div style={{flex: '1 1 180px'}}>
                  <Typography variant='body2' style={LABEL_STYLE}>Ora</Typography>
                  <TimePicker
                    disabled={disabled}
                    format='HH:mm'
                    popupStyle={{zIndex: 1400}}
                    style={{width: '100%'}}
                    value={form.takenAt ? moment(form.takenAt) : null}
                    onChange={value => onChange({...form, takenAt: value ? value.toISOString() : null})}
                  />
                </div>
              )}
            </div>
          )}

          {showBodyPart && (
            <div>
              <Typography variant='body2' style={LABEL_STYLE}>Parte del corpo (facoltativo, anche più di una)</Typography>
              {/* Free text by contract; the common answers are offered as one-tap shortcuts that
                  toggle independently, and the field below adds anything else on top of them. */}
              <div style={{display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12}}>
                {bodyPartSuggestions.map(suggestion => (
                  <Chip
                    key={suggestion}
                    label={suggestion}
                    clickable={!disabled}
                    color={form.bodyPart.includes(suggestion) ? 'primary' : 'default'}
                    variant={form.bodyPart.includes(suggestion) ? 'filled' : 'outlined'}
                    onClick={() => {
                      if (disabled) { return }
                      onChange({...form, bodyPart: toggleBodyPart(form.bodyPart, suggestion)})
                    }}
                  />
                ))}
              </div>
              <Input
                disabled={disabled}
                placeholder='Altro, specifica'
                value={getOtherBodyPart(form.bodyPart, bodyPartSuggestions)}
                onChange={event => onChange({
                  ...form,
                  bodyPart: setOtherBodyPart(form.bodyPart, bodyPartSuggestions, event.target.value)
                })}
              />
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default AssumptionResponseForm
